/**
 * Editor history store (spec 019 US4) — the single source of truth for undo / redo /
 * reset, replacing the MaskEditor's ref-based history.
 *
 * Why this fixes the reported bugs:
 *  - History is **real state** (not refs), so `canUndo`/`canRedo` and the buttons
 *    update reliably on every change (the old refs didn't drive re-render).
 *  - Snapshots are committed on **discrete edits**; a commit equal to the current
 *    snapshot is a **no-op**, so continuous drag-ticks don't flood history (the old
 *    code pushed on every prop change → "undo" reversed one micro-move).
 *  - A snapshot includes `backgroundRemoved`, so **reset** truly returns to the
 *    as-opened state (the old reset couldn't revert the background removal).
 *
 * Snapshots are compared by structural (JSON) equality — the same cheap key the old
 * code used. Geometry is small, so this is fine.
 */
import { create, type StoreApi, type UseBoundStore } from "zustand";
import type { Mask, Polygon } from "@genz/engine-bind";

/** One editor state snapshot tracked by the history. */
export interface EditorSnapshot {
  printArea: Polygon;
  cutouts: Mask[];
  /** Optional brand-logo placement quad (spec 017 US6). */
  brandArea?: Polygon | null;
  /** Whether the chroma/background removal has been baked onto the source. */
  backgroundRemoved: boolean;
}

export interface EditorHistoryState {
  entries: EditorSnapshot[];
  index: number;
  /** Start a fresh history with `snapshot` as the as-opened entry (index 0). */
  init(snapshot: EditorSnapshot): void;
  /** Record a discrete edit. No-op when equal to the current snapshot. Truncates redo tail. */
  commit(snapshot: EditorSnapshot): void;
  /** Step back one entry (clamped). */
  undo(): void;
  /** Step forward one entry (clamped). */
  redo(): void;
  /** Return to the as-opened snapshot (index 0); forward history is kept. */
  reset(): void;
  /**
   * Re-baseline `backgroundRemoved` across the WHOLE history WITHOUT recording an edit.
   *
   * Why: reopening a template REPLAYS the saved AI matte (the hosted `matte_url`), which flips
   * `backgroundRemoved` true. That is not a user change — it re-establishes the state the
   * template was SAVED in. Committing it as a new entry armed Undo, and the first Undo then
   * "reverted" to the seeded snapshot whose `backgroundRemoved:false` WIPED the restored
   * removal; the following Save emitted a matte-less bake and the cloud dropped the hosted
   * matte permanently (audit ED-1). Rewriting the flag on every entry means no undo step can
   * cross a removal boundary that was never an edit, while real in-session removals still
   * commit normally.
   */
  rebaseRemoval(backgroundRemoved: boolean): void;
}

const key = (s: EditorSnapshot): string => JSON.stringify(s);

/**
 * Build a snapshot from the editor's four live inputs.
 *
 * MN-11 / J-13: this used to be a closure inside `useEditorHistoryBridge`, which is exactly why
 * the commit effect needed a suppression — the effect's dep list named the four INPUTS but the
 * body called a function that closed over them, so `exhaustive-deps` could not see that the list
 * was already complete. As a pure function of its argument the effect can build the snapshot from
 * its own deps and the list becomes honest. Same object, same field order (the key is a
 * `JSON.stringify`, so field ORDER is part of the equality — do not reorder).
 */
export const snapshotOf = (v: {
  printArea: Polygon;
  cutouts: Mask[];
  brandArea?: Polygon | null;
  backgroundRemoved: boolean;
}): EditorSnapshot => ({
  printArea: v.printArea,
  cutouts: v.cutouts,
  brandArea: v.brandArea ?? null,
  backgroundRemoved: v.backgroundRemoved,
});

/** Structural key of a snapshot — the same cheap equality the history commits use. Exported so
 *  the MaskEditor can tell "the state has converged on the snapshot an undo/redo is driving
 *  toward" from "a fresh user edit" while an ASYNC background restore is still in flight. */
export const snapshotKey = (s: EditorSnapshot): string => key(s);

/**
 * MN-5 — a history store PER EDITOR, not one for the app.
 *
 * `useEditorHistory` below is a module-level zustand store. It happens to work because exactly one
 * MaskEditor is ever mounted and `init` runs on mount — but it is not structurally safe, there is
 * no cleanup on unmount, and the previous view's entries persist until the next mount. WS-J's F-35
 * fix REMOVES the remount that was accidentally papering over this, so the accident stops being
 * available: a view switch no longer re-`init`s by remounting, it re-`init`s deliberately.
 *
 * The singleton below is KEPT as one instance of this factory so `stores/editorStore.test.ts` and
 * any other existing reader are unaffected — it is simply no longer load-bearing.
 */
export function createEditorHistoryStore(): UseBoundStore<StoreApi<EditorHistoryState>> {
  return create<EditorHistoryState>((set, get) => ({
  entries: [],
  index: -1,
  init: (snapshot) => set({ entries: [snapshot], index: 0 }),
  commit: (snapshot) => {
    const { entries, index } = get();
    if (index >= 0 && key(entries[index]) === key(snapshot)) return; // no-op: unchanged
    const truncated = entries.slice(0, index + 1);
    truncated.push(snapshot);
    set({ entries: truncated, index: truncated.length - 1 });
  },
  undo: () => {
    const { index } = get();
    if (index > 0) set({ index: index - 1 });
  },
  redo: () => {
    const { entries, index } = get();
    if (index < entries.length - 1) set({ index: index + 1 });
  },
  reset: () => {
    if (get().entries.length > 0) set({ index: 0 });
  },
  rebaseRemoval: (backgroundRemoved) => {
    const { entries } = get();
    if (entries.length === 0) return;
    if (entries.every((e) => e.backgroundRemoved === backgroundRemoved)) return; // already the baseline
    set({
      entries: entries.map((e) =>
        e.backgroundRemoved === backgroundRemoved ? e : { ...e, backgroundRemoved },
      ),
    });
  },
  }));
}

/** The app-wide instance. Retained for compatibility; the MaskEditor uses its own (MN-5). */
export const useEditorHistory = createEditorHistoryStore();

/** Whether an undo is available. */
export const canUndo = (s: EditorHistoryState): boolean => s.index > 0;
/** Whether a redo is available. */
export const canRedo = (s: EditorHistoryState): boolean => s.index < s.entries.length - 1;
/** The current snapshot (the one undo/redo/reset point at). */
export const currentSnapshot = (s: EditorHistoryState): EditorSnapshot => s.entries[s.index];
